/* eslint-disable no-async-promise-executor */
import { DefaultValue } from 'recoil';
import type { AtomEffect } from 'recoil';
import { BroadcastChannel } from 'broadcast-channel';
import debounce from 'lodash.debounce';
import mitt from 'mitt';
import type { Emitter } from 'mitt';
import { nanoid } from 'nanoid';
import equals from 'ramda/es/equals';
import { activeWindowListener } from '../modules/activeWindow';
import localForage from 'localforage';
import { logger } from '../modules/logger';

/**
 * Anytime the atom state is updated sends a message to the other
 * atom instances on different windows and synchronizes its state.
 *
 * # Options
 *
 * > Only use these on local development, as they can themselves affect the
 * > overall performance of the synchronization process.
 *
 * ## FPS Reporter
 *
 * Since we don't have a real game loop to measure the fps, this actually measures the number of
 * messages received within a second. It means you need to spam many scroll events in order to
 * saturate the counter to the actual upper limit supported by the environment.
 *
 * - fpsReporter: boolean, if true, will log the fps in the developer tools console
 *
 * ## Delay Reporter
 *
 * This is a simple way to measure the delay between the time the message is sent and the time
 * it is received.
 *
 * - delayReporter: boolean, if true, will log the delay in the developer tools console
 * - delayThreshold: number, if fpsReporter is true, defines the threshold above which the delay will be logged
 *
 * ## Merge Function
 *
 * There are times where multiple pages might be simultaneously updating the Recoil state
 * with different values. In these cases, we can provide a merge function to provide a unified result.
 *
 * -- mergeFn: How to combine old and new values. Defaults to returning the new value unchanged.
 */

type Conductor = 'viewer' | 'reporter' | 'worklist';
export const CONDUCTORS: Partial<Record<Conductor, Conductor>> = {
  viewer: 'viewer',
  reporter: 'reporter',
  worklist: 'worklist',
};

type Options<T> = {
  fpsReporter?: boolean;
  delayReporter?: boolean;
  delayThreshold?: number;
  unidirectional?: boolean;
  mergeFn?: (oldValue: Nullable<T>, newValue: T) => T;
  conductor?: Conductor;
  debugLog?: boolean;
};

export const broadcastChannelSynchronizerEffect =
  <T>(userOptions?: Options<T> | null): AtomEffect<T> =>
  ({ node: { key }, setSelf, onSet }) => {
    const options = {
      fpsReporter: false,
      delayReporter: false,
      delayThreshold: 1000 / 20,
      unidirectional: false,
      conductor: null,
      debugLog: false,
      mergeFn: (oldValue: Nullable<T>, newValue: T) => newValue,
      ...userOptions,
    } as const;

    const bc = new BroadcastChannel(`broadcastChannelSynchronizerEffect_${key}`);

    const id = nanoid();
    let previousFrameTime = options.fpsReporter || options.delayReporter ? Date.now() : 0;
    let framesCount = 0;
    let value: T | undefined; // This will track the atoms current JSONified value

    // Listen to atom changes from other windows
    bc.onmessage = ({ type, origin, payload, now: then }) => {
      if (origin === id) {
        return;
      }

      if (type === 'set') {
        if (typeof payload !== 'string') return;

        const now = options.fpsReporter || options.delayReporter ? Date.now() : 0;

        if (options.fpsReporter) {
          if (previousFrameTime < now - 1000) {
            console.warn(
              `BroadcastChannelSynchronizerEffect: Atom with key "${key}" is updating at ${framesCount} FPS`
            );
            previousFrameTime = now;
            framesCount = 0;
          } else {
            framesCount += 1;
          }
        }

        if (options.delayReporter) {
          const delay = now - then;
          if (delay > options.delayThreshold) {
            console.warn(
              `BroadcastChannelSynchronizerEffect: Atom with key "${key}" was updated ${delay}ms later.`
            );
          }
        }

        // Recoil specifically ignores atom value changes from onSet() if they look like they were set via
        // a setSelf() call from the same atom effect. Depending on the batch, this can cause onSet() to not be called
        // when expected. Its best to only invoke setSelf() when it is actually updating the value of the atom.
        const payloadAsJSON = JSON.parse(payload);
        if (!equals(value, payloadAsJSON)) {
          const newValue = options.mergeFn(value ?? null, payloadAsJSON);
          if (options.debugLog) {
            logger.info(`[broadcastChannelSynchronizerEffect] Received sync for "${key}"`, {
              previous: value,
              next: newValue,
            });
          }
          value = newValue;
          setSelf(newValue);
        }
      } else if (type === 'ready') {
        if (options.unidirectional && !activeWindowListener.isWindowActive) return;

        if (options.conductor != null && !window.location.href.includes(options.conductor)) return;

        bc.postMessage({
          type: 'set',
          origin: id,
          payload: JSON.stringify(value),
          now: Date.now(),
        });
      }
    };

    // The logic below is called anytime the atom state is set
    onSet((newValue, oldValue) => {
      // Recoil delivers a DefaultValue instance to onSet when reset() is called on atoms with
      // selector-based or no defaults. We skip broadcasting these. Callers that need cross-window
      // reset should use set(atom, concreteDefault) instead of reset().
      if (newValue instanceof DefaultValue) return;
      if (options.unidirectional && !activeWindowListener.isWindowActive) return;
      if (options.conductor != null && !window.location.href.includes(options.conductor)) return;

      const now = options.fpsReporter || options.delayReporter ? Date.now() : 0;
      if (options.fpsReporter) {
        if (previousFrameTime < now - 1000) {
          console.warn(
            `BroadcastChannelSynchronizerEffect: Atom with key "${key}" is updating at ${framesCount} FPS`
          );
          previousFrameTime = now;
          framesCount = 0;
        } else {
          framesCount += 1;
        }
      }

      if (options.debugLog) {
        logger.info(`[broadcastChannelSynchronizerEffect] Sending sync for "${key}"`, {
          previous: oldValue,
          next: newValue,
        });
      }
      value = newValue;
      bc.postMessage({
        type: 'set',
        origin: id,
        payload: JSON.stringify(newValue),
        now: Date.now(),
      });
    });

    bc.postMessage({ type: 'ready', origin: id, now: Date.now() });

    // Close the BroadcastChannel when the atom is destroyed
    return () => {
      bc.close();
    };
  };

type LocalStorageSynchronizerEffectOptions = {
  // Only use integers, always increment by 1, useful if schema changes in non-backawrd compatible way
  version: number;
};

export const makeStoragePersisterEffectKey = (key: string, version: number = 1): string =>
  `localStoragePersisterEffect_${key}_v${version}`;

export const localStorageSynchronizerEffect =
  <T>({ version }: LocalStorageSynchronizerEffectOptions = { version: 1 }): AtomEffect<T> =>
  ({ node: { key }, setSelf }) => {
    const storageKey = makeStoragePersisterEffectKey(key, version);

    const handleStorage = (event: StorageEvent) => {
      if (
        (event.storageArea != null && event.storageArea !== window.localStorage) ||
        event.key !== storageKey
      ) {
        return;
      }

      if (event.newValue == null) {
        setSelf(new DefaultValue());
        return;
      }

      try {
        setSelf(JSON.parse(event.newValue));
      } catch (err: any) {
        logger.warn('Failed to parse localStorage-synchronized atom value', {
          key,
          storageKey,
          error: err,
        });
      }
    };

    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  };

// The following logic will remove stale localStoragePersisterEffect objects
// This logic runs once per session
export function cleanupStaleLocalStoragePersisterObjects() {
  const keys = Object.keys(localStorage).filter((key) =>
    key.startsWith('localStoragePersisterEffect_')
  );

  keys.forEach((key) => {
    try {
      const raw = localStorage.getItem(key);
      const json = raw != null ? JSON.parse(raw) : null;

      // Delete keys that have not been updated for the past ~month
      if (json?.updated != null && json.updated < Date.now() - 1000 * 60 * 60 * 24 * 30) {
        localStorage.removeItem(key);
      }
    } catch (error: any) {
      logger.warn('Failed to cleanup stale Local Storage Persister object', error);
    }
  });
}
cleanupStaleLocalStoragePersisterObjects();

/**
 * Legacy, prefer `localForagePersisterEffect` instead for new atoms
 *
 * This effect persist the atom state in localStorage but does not synchronize it
 * if you need to synchronize the state, use the broadcastChannelSynchronizerEffect
 * in combination with this one.
 * The persisted state is read from localStorage on every page load and is written to
 * localStorage on every atom state change with a debounce to avoid too many write accesses.
 */
export const localStoragePersisterEffect =
  <T>({ version }: LocalStorageSynchronizerEffectOptions = { version: 1 }): AtomEffect<T> =>
  ({ node: { key }, setSelf, onSet }) => {
    const storageKey = makeStoragePersisterEffectKey(key, version);

    // Remove the previous version, we assume we always increment versions by 1
    const previousStorageKey = makeStoragePersisterEffectKey(key, version - 1);
    localStorage.removeItem(previousStorageKey);

    const savedValue = window.localStorage.getItem(storageKey);

    if (savedValue != null) {
      // We do try...catch because in case the local storage has some invalid content
      // we don't want to crash the app but simply discard the data.
      try {
        setSelf(JSON.parse(savedValue));
      } catch (err: any) {
        console.error(err);
      }
    }

    const debouncedSetHandler = debounce(
      (newValue: T, _: unknown, isReset: boolean) => {
        const stringifiedValue = JSON.stringify(newValue);
        if (isReset || stringifiedValue === undefined) {
          try {
            if (localStorage.getItem(storageKey) != null) {
              localStorage.removeItem(storageKey);
            }
          } catch (e: any) {
            console.error(e);
            //analytics not intialized
          }
        } else {
          try {
            if (localStorage.getItem(storageKey) !== stringifiedValue) {
              localStorage.setItem(storageKey, stringifiedValue);
            }
          } catch (e: any) {
            console.error(e);
            //analytics not intialized
          }
        }
      },
      1000,
      { leading: true }
    );

    // The logic below is called anytime the atom state is set
    onSet(debouncedSetHandler);

    // Cancel the trailing debounced write when the atom is destroyed; otherwise it
    // can fire up to 1s after teardown and touch `window`/`localStorage` against a
    // dead environment, surfacing as an unhandled error. (VX-3970)
    return () => {
      debouncedSetHandler.cancel();
    };
  };

type LocalForageSynchronizerEffectOptions = {
  // Only use integers, always increment by 1, useful if schema changes in non-backawrd compatible way
  version: number;
  // IMPORTANT: new atoms should not use this option, it's here just for legacy compatibility.
  // Disables the effect, this is used just to simplify the migration of existing atoms using `localStoragePersisterEffect`
  // to `localForagePersisterEffect` without having to drastically change their test suites.
  __LEGACY_MIGRATION_DO_NOT_USE__disabled?: boolean;
};

// This effect persist the atom state in localForage but does not synchronize it
// if you need to synchronize the state, use the broadcastChannelSynchronizerEffect
// in combination with this one.
// The persisted state is read from localForage on every page load and is written to
// localForage on every atom state change with a debounce to avoid too many write accesses.
export const localForagePersisterEffect =
  <T>(
    {
      version,
      __LEGACY_MIGRATION_DO_NOT_USE__disabled: disabled,
    }: LocalForageSynchronizerEffectOptions = { version: 1 }
  ): AtomEffect<T> =>
  ({ node: { key }, setSelf, onSet }) => {
    if (disabled === true) return;

    const storageKey = makeStoragePersisterEffectKey(key, version);

    // Remove the previous version, we assume we always increment versions by 1
    const previousStorageKey = makeStoragePersisterEffectKey(key, version - 1);
    localStorage.removeItem(previousStorageKey);

    // Tracks whether the atom has been torn down so the async read below can't
    // hydrate a dead atom after teardown (which surfaces as an unhandled error). (VX-3970)
    let isDisposed = false;

    const savedValuePromise = new Promise(
      async (resolve: (result: Promise<T | DefaultValue> | T | DefaultValue) => void) => {
        const value = await localForage.getItem<{
          value: T;
        }>(storageKey);

        if (value == null || isDisposed) {
          resolve(new DefaultValue());
        } else {
          resolve(value.value);
        }
      }
    );

    // We do try...catch because in case the local storage has some invalid content
    // we don't want to crash the app but simply discard the data.
    try {
      setSelf(savedValuePromise);
    } catch (err: any) {
      console.error(err);
    }

    const debouncedSetHandler = debounce(
      (newValue: T, _: unknown, isReset: boolean) => {
        if (isReset || newValue === undefined) {
          localForage.removeItem(storageKey);
        } else {
          // We need to wrap the value in an object because localForage will
          // return `null` if the key is not found, and Recoil will interpret
          // `null` as the actual value, by wrapping it in an object we can
          // differentiate between `null` and "not present".
          localForage.setItem(storageKey, { value: newValue });
        }
      },
      1000,
      { leading: true }
    );

    // The logic below is called anytime the atom state is set
    onSet(debouncedSetHandler);

    // Cancel the trailing debounced write and flag disposal when the atom is
    // destroyed; otherwise the debounce can fire post-teardown and the pending
    // localForage read can hydrate a dead atom. (VX-3970)
    return () => {
      isDisposed = true;
      debouncedSetHandler.cancel();
    };
  };

/**
 * Takes a ref-line object and synchronizes it with the atom state.
 * Useful to access the atom from outside the React lifecycle.
 *
 * @deprecated don't use it! It's here for legacy compatibility.
 */
// @ts-expect-error [EN-7967] - TS2315 - Type 'Emitter' is not generic.
export const copyToRefEmitter: Emitter<string, unknown> = mitt();
export const copyToRefEffect =
  <T>(ref: { current: T }): AtomEffect<T> =>
  ({ node: { key }, setSelf, onSet }) => {
    // @ts-expect-error [EN-10263] - TS7006 - Parameter 'newValue' implicitly has an 'any' type.
    copyToRefEmitter.on(key, (newValue) => {
      setSelf(newValue);
      ref.current = newValue;
    });
    onSet((newValue) => {
      ref.current = newValue;
    });
  };
